import Avatar from "@mui/material/Avatar";
import { Link } from "react-router-dom";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Page from "../Components/Page";
import { useAuth } from "../Components/Auth/useAuth";
import { getPortalLabel } from "../portal/portalUtils.js";

const PORTAL_ACTIONS = {
  admin: [
    { label: "Project approvals", path: "/project-enrollments" },
    { label: "Workforce management", path: "/workforce" },
  ],
  partner: [{ label: "Project workspace", path: "/projects" }],
  aggregator: [
    { label: "Grower access", path: "/growers" },
    { label: "Excel Exchange", path: "/excel" },
  ],
};

function initials(value) {
  const parts = String(value || "User").trim().split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "U";
}

function Detail({ icon, label, value }) {
  return (
    <div className="border-l-2 border-slate-200 pl-3">
      <div className="flex items-center gap-1.5 text-xs font-bold uppercase text-slate-500">{icon}{label}</div>
      <div className="mt-1 break-words text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}

export default function WorkforceProfilePage() {
  const { user, viewer, activePortal, isImpersonating, setPersonaPortal } = useAuth();
  const name = user?.name || viewer?.displayName || viewer?.email || "User";
  const email = viewer?.email || user?.email || "Not available";
  const roles = viewer?.workforceRoles || [];
  const allowedPortals = isImpersonating ? [activePortal] : viewer?.allowedPortals || [];
  const actions = PORTAL_ACTIONS[activePortal] || [];

  return (
    <Page
      title="Workspace profile"
      headerVariant="hybrid"
      contentWidth="standard"
      padding="px-4 py-6 md:px-6 md:py-8"
      introContent={<p>Review your workforce identity, assigned roles, and available Fieldprint portals.</p>}
    >
      <div className="w-full space-y-6">
        <section className="overflow-hidden rounded-[8px] border border-slate-200 bg-white">
          <div className="border-b border-slate-200 bg-[#f7faf7] p-5 md:p-6">
            <div className="flex items-center gap-4">
              <Avatar sx={{ bgcolor: "#243245", fontSize: 22, fontWeight: 800, height: 64, width: 64 }}>{initials(name)}</Avatar>
              <div className="min-w-0">
                <h2 className="break-words text-2xl font-black text-slate-950">{name}</h2>
                <p className="mt-1 break-all text-sm text-slate-600">{email}</p>
                <span className="mt-2 inline-flex rounded-[4px] border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-800">{getPortalLabel(activePortal)} workspace</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-5 p-5 sm:grid-cols-2 md:p-6">
            <Detail icon={<EmailOutlinedIcon sx={{ fontSize: 16 }} />} label="Signed-in email" value={email} />
            <Detail icon={<BusinessOutlinedIcon sx={{ fontSize: 16 }} />} label="Current portal" value={getPortalLabel(activePortal)} />
            <Detail icon={<BadgeOutlinedIcon sx={{ fontSize: 16 }} />} label="Workforce roles" value={roles.length ? roles.join(", ") : "No workforce role assigned"} />
            <Detail icon={<LockOutlinedIcon sx={{ fontSize: 16 }} />} label="Access source" value="Field to Market Canada tenant assignment" />
          </div>
        </section>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <section className="rounded-[8px] border border-slate-200 bg-white p-5 md:p-6">
            <h2 className="text-lg font-black text-slate-950">Available portals</h2>
            <p className="mt-1 text-sm leading-6 text-slate-600">Your account can open the workspaces listed below.</p>
            <div className="mt-4 divide-y divide-slate-200 border-y border-slate-200">
              {allowedPortals.length ? allowedPortals.map((portal) => (
                portal === activePortal ? <Link className="flex items-center justify-between gap-3 py-3 text-sm font-bold text-slate-800 hover:text-[#237a43]" to="/dashboard" key={portal}>
                  {getPortalLabel(portal)} portal <ArrowForwardOutlinedIcon sx={{ fontSize: 17 }} />
                </Link> : <button className="flex w-full items-center justify-between gap-3 py-3 text-sm font-bold text-slate-800 hover:text-[#237a43]" onClick={() => setPersonaPortal(portal)} type="button" key={portal}>
                  {getPortalLabel(portal)} portal <ArrowForwardOutlinedIcon sx={{ fontSize: 17 }} />
                </button>
              )) : <p className="py-4 text-sm text-slate-500">No additional portals are assigned.</p>}
            </div>
          </section>

          <section className="rounded-[8px] border border-slate-200 bg-white p-5 md:p-6">
            <h2 className="text-lg font-black text-slate-950">Workspace access</h2>
            <p className="mt-1 text-sm leading-6 text-slate-600">Open the primary tools available for your current role.</p>
            <div className="mt-4 flex flex-col gap-2">
              {actions.map((action) => (
                <Link className="flex min-h-11 items-center justify-between rounded-[4px] border border-slate-200 px-3 py-2 text-sm font-bold text-slate-800 hover:border-[#237a43] hover:text-[#237a43]" to={action.path} key={action.path}>
                  {action.label} <ArrowForwardOutlinedIcon sx={{ fontSize: 17 }} />
                </Link>
              ))}
            </div>
          </section>
        </div>

        <div className="flex items-start gap-3 border-l-4 border-[#243245] bg-[#f5f7f8] px-4 py-3 text-sm leading-6 text-slate-600">
          <LockOutlinedIcon sx={{ color: "#243245", fontSize: 19, marginTop: "2px" }} />
          <span>Workforce roles and portal access are managed by Field to Market Canada administrators. Contact membership@fieldtomarket.ca when access needs to change.</span>
        </div>
      </div>
    </Page>
  );
}
